import React from 'react';
import styled from '@emotion/styled';
import { Text, Spacing, devices } from '../common';
import { RichTextContent } from '../StyledComponents';

type Props = {
  title?: string;
  content?: string;
  score: number;
};

const MIN_SCORE = 1;
const MAX_SCORE = 10;

const ClientSatisfaction = ({ title, content, score }: Props) => {
  const clampedScore = Math.min(MAX_SCORE, Math.max(MIN_SCORE, score));
  // Fill the gauge to the score as a percentage of the scale (e.g. 8.6 -> 86%).
  const fillRatio = clampedScore / MAX_SCORE;
  const arcLength = Math.PI * 80;
  const dashOffset = arcLength * (1 - fillRatio);

  return (
    <Container>
      <Inner>
        <GaugeColumn>
          <GaugeWrapper>
            <svg viewBox="0 0 200 120" aria-hidden="true">
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke="#E1E1E1"
                strokeWidth="14"
                strokeLinecap="round"
              />
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                fill="none"
                stroke="#0B2341"
                strokeWidth="14"
                strokeLinecap="round"
                strokeDasharray={arcLength}
                strokeDashoffset={dashOffset}
              />
            </svg>
            <ScoreDisplay>
              <ScoreValue variant="title-1" color="black" as="span">
                {clampedScore.toFixed(1).replace('.', ',')}
              </ScoreValue>
            </ScoreDisplay>
            <ScaleLabels>
              <span>{MIN_SCORE}</span>
              <span>{MAX_SCORE}</span>
            </ScaleLabels>
          </GaugeWrapper>
        </GaugeColumn>

        <TextColumn>
          {title && (
            <>
              <Text variant="title-3" color="black">
                {title}
              </Text>
              <Spacing axis="y" amount="medium" />
            </>
          )}
          {content && <BodyText>{content}</BodyText>}
        </TextColumn>
      </Inner>
    </Container>
  );
};

export default ClientSatisfaction;

const Container = styled.section`
  background-color: ${(props) => props.theme.colors.white};
  border-top: 1px solid ${(props) => props.theme.colors.lightGrey};
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: center;

  @media ${devices.laptop} {
    grid-template-columns: 1fr;
  }
`;

const GaugeColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 50px;

  @media ${devices.laptop} {
    padding: ${(props) => props.theme.spacing.xlarge}
      ${(props) => props.theme.spacing.medium} 0;
  }
`;

const GaugeWrapper = styled.div`
  position: relative;
  width: min(100%, 280px);

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const ScoreDisplay = styled.div`
  position: absolute;
  left: 50%;
  top: 58%;
  transform: translate(-50%, -50%);
`;

const ScoreValue = styled(Text)`
  max-width: none;
  font-size: 3rem;
  line-height: 1;
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 8px;
  margin-top: -4px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.grey};
`;

const TextColumn = styled.div`
  padding: 64px 50px;
  max-width: 650px;

  @media ${devices.laptop} {
    padding: ${(props) => props.theme.spacing.medium}
      ${(props) => props.theme.spacing.medium}
      ${(props) => props.theme.spacing.xlarge};
    width: 90%;
    max-width: none;
    margin: 0 auto;
  }
`;

const BodyText = styled(RichTextContent)`
  max-width: 55ch;
`;
